import React, { useCallback, useMemo, useReducer, useRef, useState } from 'react';
import { Item } from '../../../../domain/newExplorers';
import { useContextGenerator } from '../../../utils/react';
import { SelectionAction, initialSelectionState, selectionReducer } from './selectionReducer';

export interface SelectionContext {
  selectedRows: Item[];
  isSelected: (id: string) => boolean;
  activatedRow: Item | null;
  setActivatedRow: (row: Item | null) => void;
  selectionDispatch: (action: SelectionAction<Item>) => void;
}

type Context = SelectionContext | undefined;

export const SelectionContext = React.createContext<Context>(undefined);

// eslint-disable-next-line react-hooks/rules-of-hooks -- auto-generated TECH-258
export const useExplorerSelectionContext = useContextGenerator(SelectionContext, 'SelectionContext');

export interface SelectionContextProviderProps {
  children?: React.ReactNode;
}

export const SelectionContextProvider: React.FunctionComponent<SelectionContextProviderProps> = ({
  children: selectionState,
}) => {
  const [state, dispatch] = useReducer(selectionReducer<Item>, initialSelectionState<Item>());
  const [activatedRow, setActivatedRow] = useState<Item | null>(null);

  const selectedRows = useMemo(
    () => state.items.filter((item) => state.selected.has(item.id)),
    [state.items, state.selected]
  );

  const stateRef = useRef(state);
  stateRef.current = state;
  const isSelected = useCallback((id: string) => stateRef.current?.selected.has(id) ?? false, []);

  return (
    <SelectionContext.Provider
      value={{
        selectedRows,
        isSelected,
        activatedRow,
        setActivatedRow,
        selectionDispatch: dispatch,
      }}
    >
      {selectionState}
    </SelectionContext.Provider>
  );
};
